import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  viewChild,
  viewChildren,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { ConfirmationService, SelectItem } from '@openng/optimus-ui/api';
import { forkJoin, interval, Observable, Observer, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { MONLOG_DETAILS_CONTAINER } from 'src/app/constants/common.constants';
import {
  A_AND_E_LOG_CATEGORY,
  A_AND_E_MAINTENANCE,
  M_AND_R_LOG_CATEGORY,
  M_AND_R_MAINTENANCE,
  NEW_AE_CALL,
  NEW_CALL,
  NEW_CONTROLLER_CALL,
  NEW_MR_CALL,
  NEW_ONE_CALL,
  SHOW_AE,
  SHOW_MR,
} from 'src/app/constants/monlog-constants';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { CanComponentDeactivate } from 'src/app/guards/deactivate.guard';
import { MonLog } from 'src/app/model/monlogs/mon-log';
import { MonlogsApiService } from 'src/app/services/api/monlogs-api.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { MonlogsBuilderService } from 'src/app/services/builder/monlogs-builder.service';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { getMonLogColumns } from '../definitions/mon-log-table-definition';
import { MonlogsDetailContainerComponent } from './monlogs-detail-container.component';
import { MonLogsTableService } from 'src/app/services/table/monlogs-table.service';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { MonLogsAddEntryComponent } from '../components/mon-logs-add-entry.component';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { MonLogType } from 'src/app/model/admin/mon-log-type';
import { Select, SelectChangeEvent } from '@openng/optimus-ui/select';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import {
  MultiSelectChangeEvent,
  MultiSelect,
} from '@openng/optimus-ui/multiselect';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { BusinessUnitFilterComponent as BusinessUnitFilterComponent_1 } from '../../core/components/business-unit-filter/business-unit-filter.component';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { ProgressSpinnerComponent } from '../../core/components/progress-spinner.component';
import { GeneralGridComponent } from '../../core/components/general-grid.component';

@Component({
  selector: 'app-monlogs-search-container',
  templateUrl: './monlogs-search-container.component.html',
  styleUrls: ['./monlogs-search-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    BusinessUnitFilterComponent_1,
    MultiSelect,
    FormsModule,
    Select,
    ButtonDirective,
    ProgressSpinnerComponent,
    GeneralGridComponent,
    MonLogsAddEntryComponent,
    TranslatePipe,
  ],
})
export class MonlogsSearchContainerComponent
  extends SearchContainerComponent<MonLog>
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  protected _translateService = inject(TranslateService);
  protected _monLogsApi = inject(MonlogsApiService);
  protected _monLogBuilder = inject(MonlogsBuilderService);
  protected _monLogTableService = inject(MonLogsTableService);
  protected _authApi = inject(AuthApiService);
  protected _confirmationService = inject(ConfirmationService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _tabService = inject(TabService);

  readonly viewChildren = viewChildren(MonlogsDetailContainerComponent);
  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');
  readonly addEntryComponent = viewChild(MonLogsAddEntryComponent);
  allLogs: any[] = [];
  logsLoading: boolean = false;
  availableCompanies: any[] = [];
  logsLoadingEvent = new EventEmitter<any>();
  hideAE = true;
  hideMR = true;
  lastLazyEvent: any = { page: 0, pageSize: 50 };
  canEdit = false;
  canCreate = false;
  isOneCall = false;
  additionalFilters: SelectItem[] = [];
  actions: any[] = [];
  selectedAction: any;
  types: SelectItem[] = [];
  selectedFilters: string[] = [];
  exportFilename: string = '';
  activeTabChangedSub?: Subscription;
  private _isRefreshing = false;
  refreshSubscription?: Subscription;

  constructor() {
    super();
  }

  ngOnInit() {
    this.setupActiveTabChangedSubscription();
    this.setTableService(this._monLogTableService);
    this.initialize();

    const refreshInterval = interval(60000);
    this.refreshSubscription = refreshInterval.subscribe(() => {
      this.refresh(false);
    });
  }

  initialize() {
    const showAE$ = this._translateService.get('MONLOG.LABEL.SHOW_AE');
    const showMR$ = this._translateService.get('MONLOG.LABEL.SHOW_MR');
    const exportFilename$ = this._translateService.get(
      'MONLOG.EXPORT_FILE_NAME.ACTIVE_LOGS'
    );

    forkJoin([showAE$, showMR$, exportFilename$])
      .pipe(take(1))
      .subscribe((messages) => {
        this.exportFilename = messages[2];
        messages.splice(2);
        this.additionalFilters = [];
        this.additionalFilters.push({ label: messages[0], value: SHOW_AE });
        this.additionalFilters.push({ label: messages[1], value: SHOW_MR });
      });
    this.columns = getMonLogColumns();
    this.applyPreviousData();
    this.applyPreviousFilters();

    const savedFilters = this._monLogTableService.getAdditionalFilterOptions();
    if (!isNullOrUndefined(savedFilters)) {
      this.hideAE = savedFilters.hideAE;
      this.hideMR = savedFilters.hideMR;

      if (!savedFilters.hideMR) {
        this.selectedFilters.push(SHOW_MR);
      }
      if (!savedFilters.hideAE) {
        this.selectedFilters.push(SHOW_AE);
      }
    }

    this._translateService
      .get('MONLOG.SCREEN.ACTIVE_LOGS')
      .subscribe((label) => {
        this.screenName = label;
        this._tabService.updateActiveTabLabel(label);
      });

    this._monLogsApi.getMonLogTypes().subscribe((types) => {
      this.types = types
        .filter((t: MonLogType) => t.enabled === true)
        .map((t: MonLogType) => ({ label: t.name, value: t.id }));
    });

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.MONITORING_CENTER.MON_LOGS.CREATE_MON_LOGS,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.MONITORING_CENTER.MON_LOGS.EDIT_MON_LOGS,
    ]);
    this.isOneCall = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.MONITORING_CENTER.MON_LOGS.EMERGENCY_LOCATE_LOGS
        .CREATE_EMERGENCY_LOCATE_LOGS,
    ]);
    this.updateActionsList();
  }

  updateActionsList() {
    const selectAction$ = this._translateService.get(
      'MONLOG.LABEL.SELECT_ACTION'
    );
    const newCall$ = this._translateService.get(
      'MONLOG.LABEL.BUTTONS.NEW_CALL'
    );
    const newMRCall$ = this._translateService.get(
      'MONLOG.LABEL.BUTTONS.NEW_MR_CALL'
    );
    const newAECall$ = this._translateService.get(
      'MONLOG.LABEL.BUTTONS.NEW_AE_CALL'
    );
    const newControllerCall$ = this._translateService.get(
      'MONLOG.LABEL.BUTTONS.NEW_CONTROLLER_CALL'
    );
    const newOneCall$ = this._translateService.get(
      'MONLOG.LABEL.BUTTONS.NEW_ONE_CALL'
    );
    const sync$ = this._translateService.get('COMMON.LABEL.SYNC');

    let select,
      newControllerCall,
      newOneCall,
      newCall,
      newMRCall,
      newAECall,
      sync = null;
    forkJoin([
      selectAction$,
      newControllerCall$,
      newOneCall$,
      newCall$,
      newMRCall$,
      newAECall$,
      sync$,
    ])
      .pipe(take(1))
      .subscribe((messages) => {
        select = messages[0];
        newControllerCall = messages[1];
        newOneCall = messages[2];
        newCall = messages[3];
        newMRCall = messages[4];
        newAECall = messages[5];
        sync = messages[6];

        if (this.canCreate) {
          this.actions = [
            { label: select, value: 'select' },
            { label: newControllerCall, value: 'new-controller-call' },
            { label: newOneCall, value: 'new-one-call' },
            { label: newCall, value: 'new-call' },
            { label: newMRCall, value: 'new-mr-call' },
            { label: newAECall, value: 'new-ae-call' },
          ];
        } else if (this.isOneCall) {
          this.actions = [
            { label: select, value: 'select' },
            { label: newOneCall, value: 'new-one-call' },
          ];
        }

        // default actions here: Send to Day Crew, etc.
        this.actions = [...this.actions];
      });
  }

  actionEvent($event: SelectChangeEvent, object: Select) {
    if ($event.value) {
      if ($event.value === NEW_CALL) {
        this.newCall('N');
      } else if ($event.value === NEW_MR_CALL) {
        this.newCall('MR');
      } else if ($event.value === NEW_AE_CALL) {
        this.newCall('AE');
      } else if ($event.value === NEW_CONTROLLER_CALL) {
        this.newCall('I');
      } else if ($event.value === NEW_ONE_CALL) {
        this.newCall('O');
      }

      if (!isNullOrUndefined(object)) {
        object.clear();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
    this.refreshSubscription?.unsubscribe();
  }

  newCall(category = 'N') {
    const monLog = this._monLogBuilder.buildNullMonLog(category);
    let typeArray: any[] = [];
    let logId = '*New';
    if (category === 'O') {
      typeArray = [
        ...this.types.filter(
          (t) => t.label?.toLowerCase() === 'emergency locate'
        ),
      ];
    } else if (category === M_AND_R_LOG_CATEGORY) {
      typeArray = [
        ...this.types.filter(
          (t) => t.label?.toLowerCase() === M_AND_R_MAINTENANCE.toLowerCase()
        ),
      ];
      logId = '*New M&R';
    } else if (category === A_AND_E_LOG_CATEGORY) {
      typeArray = [
        ...this.types.filter(
          (t) => t.label?.toLowerCase() === A_AND_E_MAINTENANCE.toLowerCase()
        ),
      ];
      logId = '*New A&E';
    }

    monLog.MonLogTypes = typeArray;
    this._monLogTableService.setSelected(monLog);
    const tab = this._tabService.buildNewTab(MONLOG_DETAILS_CONTAINER, true);
    this._tabService.openTab(tab, true);
  }

  /**
   * Method called when a row double click event is fired.  This navigates to the details page.
   * @param $event - Event from the double click event.
   */
  changeViewEvent($event: { data: MonLog }) {
    const tab = this._tabService.buildNewTab(
      MONLOG_DETAILS_CONTAINER,
      true,
      undefined,
      $event.data.id
    );
    this._tabService.openTab(tab);
  }

  addEntryEvent($event: any) {
    if (
      $event.data.callType.toLowerCase() ===
        A_AND_E_MAINTENANCE.toLowerCase() ||
      $event.data.callType.toLowerCase() === M_AND_R_MAINTENANCE.toLowerCase()
    ) {
      this.addEntryComponent()?.setForm(
        undefined,
        true,
        true,
        $event.data.id,
        true
      );
    }

    this.addEntryComponent()?.setForm(undefined, true, true, $event.data.id);
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    let selectedBusinessUnits = [];
    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      // Handle further refreshes & changes to business unit filter component
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      // Handle initial page load (before the business unit filter subcomponent exists)
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }
    filters['businessUnits'] = selectedBusinessUnits as any;

    const query = this._monLogBuilder.buildActiveMonLogQuery(filters);
    this.queryNetwork(req, $event, query);
  }

  handleAdditionalFilter($event: MultiSelectChangeEvent) {
    const showAE = $event.value.some((i: any) => i === SHOW_AE);
    const showMR = $event.value.some((i: any) => i === SHOW_MR);
    this.hideAE = !showAE;
    this.hideMR = !showMR;
    this._monLogTableService.setAdditionalFilterOptions({
      hideAE: this.hideAE,
      hideMR: this.hideMR,
    });
    this.lazyLoad(this.lastLazyEvent);
  }

  handleBusinessUnitChange() {
    this.lazyLoad(this.lastLazyEvent);
  }

  private queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    if (!this._isRefreshing) {
      this._isRefreshing = true;

      const sort: any = {};
      if ($event.lazy && $event.lazy.sortField) {
        // sorting
        if ($event.lazy.sortField === 'statusName') {
          sort['logStatus'] =
            $event.lazy.sortOrder && $event.lazy.sortOrder === 1
              ? 'ASC'
              : 'DESC';
        } else {
          sort[$event.lazy.sortField as string] =
            $event.lazy.sortOrder && $event.lazy.sortOrder === 1
              ? 'ASC'
              : 'DESC';
        }
      } else {
        sort['createdAt'] = 'DESC';
      }

      const typesToHideArray = [];
      if (this.hideAE) {
        typesToHideArray.push(A_AND_E_MAINTENANCE);
      }
      if (this.hideMR) {
        typesToHideArray.push(M_AND_R_MAINTENANCE);
      }

      this._monLogsApi
        .queryForActiveMonLogs(
          req.pageSize,
          req.page + 1,
          query,
          sort,
          typesToHideArray
        )
        .subscribe(
          (connection) => {
            if (!connection) {
              this.elements = [];
              this.totalRecords = 0;
              this.loading = false;
              return;
            }

            this.allLogs = [
              ...connection.items.map((i: any) =>
                this._monLogBuilder.buildActiveMonLogForTable(i)
              ),
            ];

            this.elements = this.allLogs;
            this.totalRecords = connection.totalRecords;
            this.loading = false;
            this._isRefreshing = false;
          },
          (error) => {
            console.error(error);
            this._logAndMessage.translateToErrorAlert({
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
              bodyKey: 'MONLOG.MESSAGES.ERROR.LOAD_LOGS',
            });
            this.allLogs = [];
            this.elements = this.allLogs;
            this.totalRecords = 0;
            this.loading = false;
            this._isRefreshing = false;
          }
        );
    }
  }

  /**
   * This handles in application unloads (route navigation).
   */
  canDeactivate(): Observable<boolean> | boolean {
    if (!this._tabService.getActiveTab().header.startsWith('*')) {
      return true;
    } else {
      return new Observable((observer: Observer<boolean>) => {
        const unsaved$ = this._translateService.get(
          'COMMON.MESSAGES.CONFIRMATION.UNSAVED_CHANGES'
        );
        const yes$ = this._translateService.get('COMMON.LABEL.BUTTONS.LEAVE');
        const no$ = this._translateService.get('COMMON.LABEL.BUTTONS.CANCEL');
        const header$ = this._translateService.get(
          'COMMON.LABEL.HEADERS.LEAVE_PAGE'
        );
        forkJoin(unsaved$, yes$, no$, header$).subscribe((messages) => {
          this._confirmationService.confirm({
            header: messages[3],
            icon: 'fa fa-question-circle',
            acceptVisible: true,
            rejectVisible: true,
            acceptLabel: messages[1],
            rejectLabel: messages[2],
            message: messages[0],
            accept: () => {
              observer.next(true);
              observer.complete();
            },
            reject: () => {
              observer.next(false);
              observer.complete();
            },
          });
        });
      });
    }
  }

  refresh(setLoading = true): void {
    this.lazyLoad(
      { lazy: this._monLogTableService.getLastLazyLoad()! },
      setLoading
    );
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (!isNullOrUndefined(tab) && tab.header === this.screenName) {
          this.refresh();
        }
      }
    );
  }
}
